import { Plugin } from "prosemirror-state";
import { DecorationSet } from "prosemirror-view";
import { findChildren, findParentNodeOfType } from "prosemirror-utils";
import flatMap from "lodash/flatMap";
import { createSectionDecorations } from "../helpers";
/**
 * Maximum level of indentation allowed
 */
const MAX_INDENTATION = 16;
/**
 * Nodes that should ignore indentation
 */
const UNINDENTABLE_NODES = [
    "callout",
    "table",
    "horizontal_rule",
    "image",
];
/**
 * Manage decorations for heading indentation.
 *
 * Indented headings should make all content inside their section to be indented as well.
 * We achieve that by applying decorations to the nodes.
 *
 * A node is considered to be inside the section of its previous sibling heading, based on the level of the heading.
 */
export function HeadingIndentationPlugin() {
    return new Plugin({
        state: {
            init(config, state) {
                return createIndentationDecorations(state);
            },
            apply(transaction, set, oldState, newState) {
                if (!transaction.docChanged) {
                    set = set.map(transaction.mapping, transaction.doc);
                    return set;
                }
                return createIndentationDecorations(newState);
            },
        },
        props: {
            decorations(state) {
                return this.getState(state);
            },
        },
    });
}
/**
 * Look for all headings nodes and create a `indentation` DecorationSet for the nodes inside their section.
 */
export function createIndentationDecorations(state) {
    const nodes = findChildren(state.doc, (node) => node.type === state.schema.nodes.heading &&
        !!node.attrs.indentation, true);
    const decorations = flatMap(nodes, ({ node, pos }) => createSectionDecorations(state, { node, pos }, ({ indentation }, targetNode) => {
        if (!UNINDENTABLE_NODES.includes(targetNode.type.name) &&
            indentation) {
            return { style: `margin-left: ${indentation * 24}px` };
        }
        return null;
    }, true));
    return DecorationSet.create(state.doc, decorations);
}
/**
 * Return a function that changes the indentation attribute of node inside current selection.
 *
 * @param nodeType Only match this nodeType
 * @param delta The indentation delta that should be added (or subtracted when negative)
 */
export const changeNodeIndentation = (nodeType, delta) => (state, dispatch, view) => {
    var _a;
    const target = findParentNodeOfType(nodeType)(state.selection);
    if (!target) {
        return false;
    }
    const { attrs } = target.node;
    const indentation = ((_a = attrs.indentation) !== null && _a !== void 0 ? _a : 0) + delta;
    const tr = view.state.tr;
    tr.setNodeMarkup(target.pos, null, {
        ...attrs,
        indentation: Math.max(0, Math.min(indentation, MAX_INDENTATION)),
    });
    dispatch(tr);
    return true;
};
